import { labelDefaultMediumStyles, labelDefaultRegularStyles } from '@lifebit-ai/lds'
import styled, { type DefaultTheme } from 'styled-components'

import { ImportRequestScanResult } from 'services/import-requests/enums'

const iconColorByScanResult = (theme: DefaultTheme, scanResult: ImportRequestScanResult): string => {
  switch (scanResult) {
    case ImportRequestScanResult.Clean:
      return theme.color.feedback.success.default
    case ImportRequestScanResult.ThreatDetected:
      return theme.color.feedback.danger.default
    case ImportRequestScanResult.ScanFailed:
    case ImportRequestScanResult.NotStarted:
      return theme.color.feedback.warning.default
    case ImportRequestScanResult.InProgress:
    default:
      return theme.color.feedback.action.default
  }
}

/** Narrower than the main column's metadata: this card sits in the 450px rail. */
export const Row = styled.div`
  align-items: baseline;
  display: grid;
  gap: ${({ theme }) => theme.size.space['04']};
  grid-template-columns: 140px 1fr;
`

export const Label = styled.span`
  ${labelDefaultRegularStyles}
  color: ${({ theme }) => theme.color.content.secondary};
`

export const Value = styled.span`
  ${labelDefaultMediumStyles}
  color: ${({ theme }) => theme.color.content.main};
  min-width: 0;
  word-break: break-word;
`

/**
 * A state that went wrong is a sentence, not a value: it wraps, so it is set in regular weight and
 * in its own colour. Everything else reads like the values around it.
 */
const isProblem = (scanResult: ImportRequestScanResult): boolean =>
  scanResult === ImportRequestScanResult.ThreatDetected || scanResult === ImportRequestScanResult.ScanFailed

const textColorByScanResult = (theme: DefaultTheme, scanResult: ImportRequestScanResult): string => {
  switch (scanResult) {
    case ImportRequestScanResult.ThreatDetected:
      return theme.color.feedback.danger.default
    case ImportRequestScanResult.ScanFailed:
      return theme.color.feedback.warning.default
    default:
      return theme.color.content.main
  }
}

export const StateValue = styled(Value)<{ $scanResult: ImportRequestScanResult }>`
  align-items: flex-start;
  color: ${({ theme, $scanResult }) => textColorByScanResult(theme, $scanResult)};
  display: inline-flex;
  gap: ${({ theme }) => theme.size.space['02']};
  ${({ $scanResult }) => ($scanResult && isProblem($scanResult) ? labelDefaultRegularStyles : '')}

  svg {
    flex-shrink: 0;
    height: ${({ theme }) => theme.size.icon.small};
    width: ${({ theme }) => theme.size.icon.small};
    /*
     * Sits on the first line rather than centred against a message that wraps to two. The literal
     * is the fallback for browsers without the lh unit, which would otherwise drop the rule.
     */
    margin-top: 3px;
    margin-top: calc((1lh - ${({ theme }) => theme.size.icon.small as string}) / 2);

    path {
      fill: ${({ theme, $scanResult }) => iconColorByScanResult(theme, $scanResult)};
    }
  }
`

export const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.size.space['03']};
`
